import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAppStore } from "@/lib/store";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { BottomNav } from "@/components/BottomNav";
import { TestList, type TestProject } from "@/features/tests/TestList";
import { TestPreviewModal } from "@/features/tests/TestPreviewModal";
import { TestApplyDialog } from "@/features/tests/TestApplyDialog";
import { getTestProjectsByCategory } from "@/features/tests/testData";
import { Button } from "@/components/ui/button";
import { Smartphone, Monitor, ShieldCheck, Zap, Wallet, ArrowRight } from "lucide-react";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "PataTasks — App & Web Testing" },
      {
        name: "description",
        content:
          "PataTasks is a modern app and web testing platform. Earn money by testing digital products and sharing structured feedback.",
      },
      { property: "og:title", content: "PataTasks — App & Web Testing" },
      {
        property: "og:description",
        content:
          "PataTasks is a modern app and web testing platform. Earn money by testing digital products and sharing structured feedback.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://patatasks.app/" },
      { property: "og:image", content: "https://patatasks.app/patataskslogo.png" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "PataTasks — App & Web Testing" },
      {
        name: "twitter:description",
        content:
          "PataTasks is a modern app and web testing platform. Earn money by testing digital products and sharing structured feedback.",
      },
      { name: "theme-color", content: "#2563eb" },
      { name: "fameswap-verification", content: "LJEjvgk57AAzNPaM02cw0UqyaX2SCjAY" },
      {
        tagName: "script",
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "PataTasks",
          description:
            "PataTasks is a modern app and web testing platform. Earn money by testing digital products and sharing structured feedback.",
          url: "https://patatasks.app/",
          potentialAction: {
            "@type": "SearchAction",
            target: "https://patatasks.app/search?q={search_term_string}",
            "query-input": "required name=search_term_string",
          },
        }),
      },
    ],
  }),
  component: HomePage,
});

function HomePage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [selectedProject, setSelectedProject] = useState<TestProject | null>(null);
  const [paymentProject, setPaymentProject] = useState<TestProject | null>(null);
  const isAuthenticated = useAppStore((s) => s.isAuthenticated);
  const user = useAppStore((s) => s.user);
  const balance = Number((user as any)?.balance ?? 0);
  const maintenancePaid = Number(
    (user as any)?.maintenancePaid ?? (user as any)?.maintenance_paid ?? 0,
  );
  const lockThreshold = Number(
    (user as any)?.lockThreshold ?? (user as any)?.lock_threshold ?? 800,
  );
  const isLocked = balance >= lockThreshold && maintenancePaid === 0 && user?.isAdmin !== 1;
  const lockThresholdUsd = lockThreshold / 100;

  const featuredProjects = [
    ...getTestProjectsByCategory("app-tests").slice(0, 3),
    ...getTestProjectsByCategory("web-tests").slice(0, 3),
  ];

  const filteredProjects = getTestProjectsByCategory().filter(
    (p) =>
      p.title.toLowerCase().includes(search.toLowerCase()) ||
      p.appName.toLowerCase().includes(search.toLowerCase()) ||
      p.instructions.toLowerCase().includes(search.toLowerCase()),
  );

  const categories = [
    { name: "App Tests", href: "/app-tests", icon: Smartphone, color: "bg-emerald-600" },
    { name: "Web App Tests", href: "/webapp-tests", icon: Monitor, color: "bg-blue-600" },
  ];

  const handleLockedClick = (project: TestProject) => {
    if (!isAuthenticated) {
      window.location.href = "/login";
      return;
    }
    setPaymentProject(project);
  };

  return (
    <div className="bg-background pb-20 md:pb-0">
      <div className="mx-auto max-w-7xl px-4 py-6">
        <div className="relative overflow-hidden rounded-3xl bg-linear-to-br from-blue-700 via-blue-600 to-indigo-700 p-8 text-white shadow-xl mb-10">
          <div className="absolute inset-0 opacity-10">
            <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" className="h-full w-full">
              <path
                fill="currentColor"
                d="M44.7,-76.4C58.8,-69.2,71.8,-59.1,81.6,-46.6C91.4,-34.1,98.1,-19.2,95.8,-5.3C93.5,8.6,82.2,21.5,71.6,32.4C61,43.3,51.1,52.2,40,59.2C28.9,66.2,16.6,71.3,3.6,69.8C-9.4,68.3,-21.2,60.1,-32.8,52.1C-44.4,44.1,-55.8,36.3,-65.8,26.2C-75.8,16.1,-84.4,3.7,-82.8,-7.2C-81.2,-18.1,-69.4,-27.5,-58.1,-35.9C-46.8,-44.3,-36,-51.7,-24.3,-57.6C-12.6,-63.5,-0.1,-67.9,13.4,-71.5C26.9,-75.1,30.6,-83.6,44.7,-76.4Z"
                transform="translate(100 100)"
              />
            </svg>
          </div>
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-4">
              <div>
                <h1 className="text-3xl font-bold">PataTasks</h1>
                <p className="text-blue-100">App & Web Testing Platform</p>
              </div>
            </div>
            <p className="text-blue-50 mb-6 text-base leading-relaxed">
              PataTasks is a modern app and web testing platform. Earn money by testing digital
              products for third-party providers. Browse tests across music, social, browsing,
              education, and more.
            </p>
            <div className="flex flex-wrap gap-3">
              {isAuthenticated ? (
                <>
                  <Link to="/wallet">
                    <Button className="bg-white text-blue-700 hover:bg-green-600 hover:text-white px-6">
                      My Wallet <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                  <Link to="/profile">
                    <Button className="bg-blue-600 hover:bg-green-600 text-white px-6 border border-white *:active:bg-blue-900">
                      My Profile
                    </Button>
                  </Link>
                </>
              ) : (
                <>
                  <Link to="/signup">
                    <Button className="bg-white text-blue-700 hover:bg-green-600 hover:text-white px-6">
                      Get Started <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                  <Link to="/signup">
                    <Button className="bg-blue-600 hover:bg-green-600 text-white px-6 border border-white *:active:bg-blue-900">
                      Create Account
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="mb-10">
          <div className="relative mb-6">
            <input
              type="text"
              placeholder="Search test projects..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-xl border bg-white px-4 py-3 text-sm sm:text-base shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 h-12"
            />
          </div>
          {search ? (
            <div>
              <h2 className="text-lg font-semibold mb-4">Search Results</h2>
              <TestList
                projects={filteredProjects}
                onPreview={setSelectedProject}
                isLocked={isLocked}
                onLockedClick={handleLockedClick}
              />
            </div>
          ) : (
            <div>
              <h2 className="text-lg font-semibold mb-4">Featured Tests</h2>
              <TestList
                projects={featuredProjects}
                onPreview={setSelectedProject}
                initialVisibleCount={6}
                isLocked={isLocked}
                onLockedClick={handleLockedClick}
              />
            </div>
          )}
        </div>

        <div className="mb-10">
          <h2 className="text-lg font-semibold mb-4 text-center">Browse by Category</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-2">
            {categories.map((cat) => (
              <Link
                key={cat.name}
                to={cat.href}
                className="flex flex-col items-center gap-3 rounded-2xl border bg-white p-6 shadow-sm transition-all hover:shadow-md"
              >
                <div
                  className={`flex h-12 w-12 items-center justify-center rounded-xl ${cat.color} text-white`}
                >
                  <cat.icon className="h-6 w-6" />
                </div>
                <span className="font-semibold text-sm">{cat.name}</span>
              </Link>
            ))}
          </div>
        </div>

        {!isAuthenticated && (
          <div className="mb-8 rounded-3xl border border-blue-300 bg-linear-to-br from-blue-50 to-indigo-50 p-8 shadow-sm">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-foreground mb-2">Ready to start testing?</h2>
              <p className="text-slate-600 max-w-xl mx-auto">
                Create an account, complete a short onboarding interview, and start providing
                structured feedback to improve digital products.
              </p>
            </div>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/signup">
                <Button className="bg-blue-600 hover:bg-green-600 text-white px-6">
                  Create Account <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/login">
                <Button
                  variant="outline"
                  className="border-blue-900 text-slate-700 hover:bg-blue-900/10 px-6"
                >
                  Login
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
      <TestPreviewModal
        project={selectedProject}
        onClose={() => setSelectedProject(null)}
        onStartTest={() => {
          if (selectedProject) {
            localStorage.setItem("pending_test_id", selectedProject.id);
            navigate({ to: "/submit-test" });
          }
        }}
      />
      <TestApplyDialog
        project={paymentProject!}
        open={!!paymentProject}
        onClose={() => setPaymentProject(null)}
        onSuccess={() => {
          setPaymentProject(null);
          if (paymentProject) {
            localStorage.setItem("pending_test_id", paymentProject.id);
            navigate({ to: "/submit-test" });
          }
        }}
        lockThresholdUsd={lockThresholdUsd}
      />
      <BottomNav />
    </div>
  );
}
